import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta, type PageRequest } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { DEFAULT_PRODUCT_PAGE_SIZE } from '../constants/product.constants';
import {
  assignedInstituteListSchema,
  bookEditionResponseSchema,
  bookTypeResponseSchema,
  dcrSheetSchema,
  distributionInstituteListSchema,
  distributionMemberListSchema,
  specimenDistributionResponseSchema,
  specimenInstitutionResponseSchema,
  specimenResponseSchema,
  type AssignedInstitute,
  type BookEditionPayload,
  type BookEditionResponse,
  type BookTypePayload,
  type BookTypeResponse,
  type CatalogueSearch,
  type DcrSheet,
  type DcrSheetParams,
  type DcrSheetSearch,
  type DistributionInstitute,
  type DistributionMember,
  type DistributionMemberParams,
  type EntryTeacherNamePayload,
  type SpecimenDistributionResponse,
  type SpecimenDistributionRow,
  type SpecimenDistributionSearch,
  type SpecimenInstitutionResponse,
  type SpecimenInstitutionSearch,
  type SpecimenListParams,
  type SpecimenListSearch,
  type SpecimenPayload,
  type SpecimenResponse,
} from '../types/product.types';

const ISO_DATE_LENGTH = 10;

const stamp = (): string => new Date().toISOString().slice(0, ISO_DATE_LENGTH);

export const productQueryKeys = {
  all: () => ['product'] as const,
  distribution: (params: SpecimenDistributionSearch) =>
    [...productQueryKeys.all(), 'distribution', params] as const,
  distributionAllRows: (params: SpecimenDistributionSearch) =>
    [...productQueryKeys.all(), 'distribution', 'all', params] as const,
  distributedSpecimen: (specimenId: number, params: DistributionMemberParams) =>
    [...productQueryKeys.all(), 'distributed-specimen', params, specimenId] as const,
  distributionMembers: (specimenId: number, params: DistributionMemberParams) =>
    [...productQueryKeys.all(), 'distribution-members', params, specimenId] as const,
  distributionInstitutes: (specimenId: number, userId: number, params: DistributionMemberParams) =>
    [...productQueryKeys.all(), 'distribution-institutes', params, specimenId, userId] as const,
  specimenInstitutionList: (params: SpecimenInstitutionSearch) =>
    [...productQueryKeys.all(), 'specimen-institution', 'list', params] as const,
  dcrSheet: (params: DcrSheetParams) => [...productQueryKeys.all(), 'dcr-sheet', params] as const,
  assignedInstitutes: (params: DcrSheetSearch, visited: boolean) =>
    [...productQueryKeys.all(), 'assigned-institutes', params, visited] as const,
  bookTypes: (params: CatalogueSearch & PageRequest) =>
    [...productQueryKeys.all(), 'book-types', params] as const,
  bookTypeList: (params: CatalogueSearch) =>
    [...productQueryKeys.all(), 'book-types', 'list', params] as const,
  bookEditions: (params: CatalogueSearch & PageRequest) =>
    [...productQueryKeys.all(), 'book-editions', params] as const,
  bookEditionList: (params: CatalogueSearch) =>
    [...productQueryKeys.all(), 'book-editions', 'list', params] as const,
  specimens: (params: SpecimenListSearch & PageRequest) =>
    [...productQueryKeys.all(), 'specimens', params] as const,
  specimenList: (params: SpecimenListParams) =>
    [...productQueryKeys.all(), 'specimens', 'list', params] as const,
};

export const specimenDistributionInfiniteQueryOptions = (params: SpecimenDistributionSearch) =>
  infiniteQueryOptions({
    queryKey: productQueryKeys.distribution(params),
    queryFn: async ({ pageParam }): Promise<SpecimenDistributionResponse> => {
      const res = await apiClient.get(API.product.specimenDistribution, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PRODUCT_PAGE_SIZE },
      });
      return specimenDistributionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const specimenDistributionAllRowsQueryOptions = (params: SpecimenDistributionSearch) =>
  queryOptions({
    queryKey: productQueryKeys.distributionAllRows(params),
    queryFn: async (): Promise<SpecimenDistributionResponse> => {
      const res = await apiClient.get(API.product.specimenDistributionAll, { params });
      return specimenDistributionResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const distributedSpecimenQueryOptions = (
  specimenId: number,
  params: DistributionMemberParams,
) =>
  queryOptions({
    queryKey: productQueryKeys.distributedSpecimen(specimenId, params),
    queryFn: async (): Promise<SpecimenDistributionRow | null> => {
      const res = await apiClient.get(API.product.specimenDistribution, {
        params: { ...params, specimenId, page: FIRST_PAGE, pageSize: 1 },
      });
      return specimenDistributionResponseSchema.parse(res.data).data[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const distributionMembersQueryOptions = (
  specimenId: number,
  params: DistributionMemberParams,
) =>
  queryOptions({
    queryKey: productQueryKeys.distributionMembers(specimenId, params),
    queryFn: async (): Promise<DistributionMember[]> => {
      const res = await apiClient.get(API.product.distributionMembers(specimenId), { params });
      return distributionMemberListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const distributionInstitutesQueryOptions = (
  specimenId: number,
  userId: number,
  params: DistributionMemberParams,
) =>
  queryOptions({
    queryKey: productQueryKeys.distributionInstitutes(specimenId, userId, params),
    queryFn: async (): Promise<DistributionInstitute[]> => {
      const res = await apiClient.get(API.product.distributionInstitutes(specimenId, userId), {
        params,
      });
      return distributionInstituteListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const specimenInstitutionInfiniteQueryOptions = (params: SpecimenInstitutionSearch) =>
  infiniteQueryOptions({
    queryKey: productQueryKeys.specimenInstitutionList(params),
    queryFn: async ({ pageParam }): Promise<SpecimenInstitutionResponse> => {
      const res = await apiClient.get(API.product.specimenInstitution, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PRODUCT_PAGE_SIZE },
      });
      return specimenInstitutionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const dcrSheetQueryOptions = (params: DcrSheetParams) =>
  queryOptions({
    queryKey: productQueryKeys.dcrSheet(params),
    queryFn: async (): Promise<DcrSheet> => {
      const res = await apiClient.get(API.product.dcrSheet, { params });
      return dcrSheetSchema.parse(res.data);
    },
    enabled: params.userId !== undefined,
    staleTime: STALE_TIME,
  });

export const assignedInstitutesQueryOptions = (params: DcrSheetSearch, visited: boolean) =>
  queryOptions({
    queryKey: productQueryKeys.assignedInstitutes(params, visited),
    queryFn: async (): Promise<AssignedInstitute[]> => {
      const res = await apiClient.get(API.product.dcrSheetInstitutes, {
        params: { ...params, visited },
      });
      return assignedInstituteListSchema.parse(res.data);
    },
    enabled: params.userId !== undefined,
    staleTime: STALE_TIME,
  });

export const bookTypesQueryOptions = (params: CatalogueSearch & PageRequest) =>
  queryOptions({
    queryKey: productQueryKeys.bookTypes(params),
    queryFn: async (): Promise<BookTypeResponse> => {
      const res = await apiClient.get(API.product.bookTypes, { params });
      return bookTypeResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const bookTypesInfiniteQueryOptions = (params: CatalogueSearch) =>
  infiniteQueryOptions({
    queryKey: productQueryKeys.bookTypeList(params),
    queryFn: async ({ pageParam }): Promise<BookTypeResponse> => {
      const res = await apiClient.get(API.product.bookTypes, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PRODUCT_PAGE_SIZE },
      });
      return bookTypeResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const bookEditionsQueryOptions = (params: CatalogueSearch & PageRequest) =>
  queryOptions({
    queryKey: productQueryKeys.bookEditions(params),
    queryFn: async (): Promise<BookEditionResponse> => {
      const res = await apiClient.get(API.product.bookEditions, { params });
      return bookEditionResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const bookEditionsInfiniteQueryOptions = (params: CatalogueSearch) =>
  infiniteQueryOptions({
    queryKey: productQueryKeys.bookEditionList(params),
    queryFn: async ({ pageParam }): Promise<BookEditionResponse> => {
      const res = await apiClient.get(API.product.bookEditions, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PRODUCT_PAGE_SIZE },
      });
      return bookEditionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const specimensQueryOptions = (params: SpecimenListSearch & PageRequest) =>
  queryOptions({
    queryKey: productQueryKeys.specimens(params),
    queryFn: async (): Promise<SpecimenResponse> => {
      const res = await apiClient.get(API.product.specimens, { params });
      return specimenResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const specimensInfiniteQueryOptions = (params: SpecimenListParams) =>
  infiniteQueryOptions({
    queryKey: productQueryKeys.specimenList(params),
    queryFn: async ({ pageParam }): Promise<SpecimenResponse> => {
      const res = await apiClient.get(API.product.specimens, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PRODUCT_PAGE_SIZE },
      });
      return specimenResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function exportSpecimenAdjustment(params: SpecimenDistributionSearch): Promise<void> {
  const res = await apiClient.get(API.product.specimenDistributionExport, {
    params,
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `specimen-adjustment-${stamp()}.xlsx`);
}

export async function exportSpecimenInstitution(params: SpecimenInstitutionSearch): Promise<void> {
  const res = await apiClient.get(API.product.specimenInstitutionExport, {
    params,
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `specimen-institution-${stamp()}.xlsx`);
}

export async function exportSpecimenList(params: SpecimenListParams): Promise<void> {
  const res = await apiClient.get(API.product.specimensExport, {
    params,
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `specimen-list-${stamp()}.xlsx`);
}

export async function updateEntryTeacherName(
  entryId: number,
  payload: EntryTeacherNamePayload,
): Promise<void> {
  await apiClient.patch(API.product.entryTeacherName(entryId), payload);
}

export async function createBookType(payload: BookTypePayload): Promise<void> {
  await apiClient.post(API.product.bookTypes, payload);
}

export async function updateBookType(id: number, payload: BookTypePayload): Promise<void> {
  await apiClient.put(API.product.bookType(id), payload);
}

export async function deleteBookType(id: number): Promise<void> {
  await apiClient.delete(API.product.bookType(id));
}

export async function createBookEdition(payload: BookEditionPayload): Promise<void> {
  await apiClient.post(API.product.bookEditions, payload);
}

export async function updateBookEdition(id: number, payload: BookEditionPayload): Promise<void> {
  await apiClient.put(API.product.bookEdition(id), payload);
}

export async function deleteBookEdition(id: number): Promise<void> {
  await apiClient.delete(API.product.bookEdition(id));
}

export async function createSpecimen(payload: SpecimenPayload): Promise<void> {
  await apiClient.post(API.product.specimens, payload);
}

export async function updateSpecimen(id: number, payload: SpecimenPayload): Promise<void> {
  await apiClient.put(API.product.specimen(id), payload);
}

export async function deleteSpecimen(id: number): Promise<void> {
  await apiClient.delete(API.product.specimen(id));
}
